import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useAuth } from '@/hooks/useAuth';
import { Section, timeLabel } from '../ui';
import type { Workspace } from '../types';

/**
 * Laboratory for an ED patient is the existing LIS, unchanged: the order
 * carries the ER visit, defaults to the branch's ED priority (STAT), and a
 * critical value is routed to the encounter's ATTENDING while the patient is
 * still in the ED (utils/labCritical.js, D14). Results, verification and
 * acknowledgement happen in the Laboratory module as for any other order.
 */
export default function LabsSection({ ws, onOrder }: { ws: Workspace; onOrder: () => void }) {
  const { user } = useAuth();
  const canOrder = !!user?.modules?.includes('lab:orders') && ws.encounter.status !== 'departed';
  return (
    <div className="grid gap-3 lg:grid-cols-2">
      <Section title="Lab orders" actions={canOrder ? <Button size="sm" onClick={onOrder}>🧪 New order</Button> : undefined}>
        {ws.labs.length === 0 ? <div className="text-[13px] text-ink-muted">No laboratory orders.</div> : (
          <div className="space-y-2">
            {ws.labs.map((l) => (
              <div key={l.id} className="rounded-lg border border-border p-2.5 text-[12px]">
                <div className="flex flex-wrap items-center gap-2">
                  <b>{l.order_number}</b>
                  <Badge variant={l.priority === 'stat' ? 'danger' : l.priority === 'urgent' ? 'warn' : 'neutral'}>{l.priority.toUpperCase()}</Badge>
                  <Badge variant={l.status === 'REPORTED' ? 'success' : 'info'}>{l.status.toLowerCase().replace(/_/g, ' ')}</Badge>
                  <span className="ml-auto text-ink-muted">{timeLabel(l.ordered_at)}</span>
                </div>
                <div className="mt-1 text-ink-muted">{l.items.map((i) => `${i.test_name} (${i.status.toLowerCase().replace(/_/g, ' ')})`).join(' · ')}</div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 text-[11px] text-ink-muted">Results are entered, verified and released in the Laboratory module.</div>
      </Section>
      <Section title="Critical results">
        {ws.critical_results.length === 0 ? <div className="text-[13px] text-ink-muted">None.</div> : (
          <ul className="space-y-1.5 text-[12px]">
            {ws.critical_results.map((c) => (
              <li key={c.id} className={c.acknowledged_at ? 'text-ink-muted' : 'font-semibold text-danger-ink'}>
                ❗ {c.parameter_name} {c.value_display} ({c.test_name}) · {timeLabel(c.detected_at)}
                {c.acknowledged_at ? ` — acknowledged by ${c.acknowledged_by} at ${timeLabel(c.acknowledged_at)}` : ` — awaiting acknowledgement${c.responsible_doctor_name ? ` by ${c.responsible_doctor_name}` : ''}`}
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
